import React, { useContext, useState } from 'react'
import TracksContext from './TracksContext'

/**
 * Tells the LOCAL user when their camera could not be started.
 *
 * When the camera fails (permission denied, device busy, an in-app browser without camera
 * access...) the call still joins with audio only. Before this notice existed nothing was shown,
 * so the participant had no idea the other side was looking at a placeholder icon instead of
 * their video. The notice explains why and offers a retry that does not drop the call.
 */
const MediaDeviceNotice: React.FC = () => {
  const { isAudioOnly, videoTrackState, retryTrackInitialization } =
    useContext(TracksContext)
  const [retrying, setRetrying] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  if (!isAudioOnly || dismissed) return null

  const reason =
    videoTrackState?.error?.message ||
    'Your camera could not be started. Please allow camera access.'

  const handleRetry = async () => {
    if (retrying || !retryTrackInitialization) return
    setRetrying(true)
    try {
      await retryTrackInitialization()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div role='alert' style={styles.container}>
      <span style={styles.text}>
        {reason} The other participant can hear you but cannot see you.
      </span>
      <button
        type='button'
        onClick={handleRetry}
        disabled={retrying}
        style={{ ...styles.button, opacity: retrying ? 0.7 : 1 }}
      >
        {retrying ? 'Starting camera…' : 'Retry camera'}
      </button>
      <button
        type='button'
        onClick={() => setDismissed(true)}
        aria-label='Dismiss camera notice'
        style={styles.dismiss}
      >
        ×
      </button>
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: '0 0 auto',
    margin: '8px 12px',
    padding: '10px 14px',
    borderRadius: 10,
    background: '#fff7ed',
    border: '1px solid #fdba74',
    color: '#7c2d12',
    fontSize: 13,
    lineHeight: 1.4,
    zIndex: 120
  } as React.CSSProperties,
  text: {
    flex: 1
  } as React.CSSProperties,
  button: {
    flexShrink: 0,
    padding: '6px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#ea580c',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer'
  } as React.CSSProperties,
  dismiss: {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: '#7c2d12',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer'
  } as React.CSSProperties
}

export default MediaDeviceNotice
